import { fetch_user_profile_details, update_user_profile_details } from '@/services/user_profile_service';

function read_session_number(...keys) {
  if (typeof window === 'undefined') return null;

  for (const key of keys) {
    const value = window.sessionStorage.getItem(key);
    if (value === null || value === undefined || value === '') continue;

    const parsed_value = Number(value);
    if (!Number.isNaN(parsed_value) && parsed_value > 0) {
      return parsed_value;
    }
  }

  return null;
}

function get_initials(name = '') {
  return String(name || '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U';
}

function format_date(date_value) {
  const normalized_value = String(date_value || '').trim();
  if (!normalized_value) return 'Not available';

  const parsed_date = new Date(normalized_value);
  if (Number.isNaN(parsed_date.getTime())) return normalized_value;

  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(parsed_date);
}

function has_valid_name(name) {
  return /^[A-Za-z][A-Za-z\s'-]{1,49}$/.test(String(name || '').trim());
}

function is_valid_phone_number(phone_number) {
  return /^\d{10}$/.test(String(phone_number || '').trim());
}

export const userProfileModalMixin = {
  data() {
    return {
      show_profile_modal: false,
      is_loading_profile: false,
      is_saving_profile: false,
      is_editing_profile: false,
      profile_error: '',
      profile_save_error: '',
      profile_details: null,
      profile_edit_form: {
        first_name: '',
        last_name: '',
        phone_number: ''
      }
    };
  },
  computed: {
    current_user_id() {
      return this.$store.getters.currentUserId || read_session_number('current_user_id', 'user_id');
    },
    profile_fallback_name() {
      return this.user_name || this.current_user_display_name || 'User';
    },
    profile_display_name() {
      return String(this.profile_details?.full_name || '').trim() || this.profile_fallback_name;
    },
    profile_initials() {
      return get_initials(this.profile_display_name);
    },
    profile_first_name() {
      return String(this.profile_details?.first_name || '').trim() || 'Not available';
    },
    profile_last_name() {
      return String(this.profile_details?.last_name || '').trim() || 'Not available';
    },
    profile_email() {
      return String(this.profile_details?.email || '').trim() || 'Not available';
    },
    profile_phone_number() {
      return String(this.profile_details?.phone_number || '').trim() || 'Not available';
    },
    profile_joined_date() {
      return format_date(this.profile_details?.date_joined);
    },
    profile_status_label() {
      return this.profile_details?.is_active ? 'Active' : 'Inactive';
    },
    profile_modal_data() {
      return {
        initials: this.profile_initials,
        display_name: this.profile_display_name,
        first_name: this.profile_first_name,
        last_name: this.profile_last_name,
        email: this.profile_email,
        phone_number: this.profile_phone_number,
        joined_date: this.profile_joined_date,
        status_label: this.profile_status_label
      };
    },
    can_edit_profile() {
      return Boolean(this.profile_details?.id);
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handle_escape_key);
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handle_escape_key);
    }
  },
  methods: {
    sync_profile_edit_form() {
      this.profile_edit_form = {
        first_name: String(this.profile_details?.first_name || '').trim(),
        last_name: String(this.profile_details?.last_name || '').trim(),
        phone_number: String(this.profile_details?.phone_number || '').trim()
      };
    },
    async open_profile_modal() {
      this.show_profile_modal = true;
      this.profile_error = '';
      this.profile_save_error = '';

      if (!this.current_user_id) {
        this.profile_details = null;
        this.profile_error = 'We could not find the logged-in user profile.';
        return;
      }

      if (Number(this.profile_details?.id || 0) === Number(this.current_user_id)) {
        return;
      }

      this.is_loading_profile = true;
      try {
        const profile = await fetch_user_profile_details(this.current_user_id);
        this.profile_details = profile;
        this.sync_profile_edit_form();

        if (profile.full_name && profile.full_name !== this.$store.getters.currentLeadName) {
          this.$store.dispatch('setCurrentLeadName', profile.full_name);
        }

        if (!this.$store.getters.currentUserId) {
          this.$store.dispatch('setCurrentUserId', this.current_user_id);
        }
      } catch (error) {
        this.profile_details = null;
        this.profile_error = String(error?.message || 'Unable to load your profile right now.');
      } finally {
        this.is_loading_profile = false;
      }
    },
    open_profile_edit() {
      this.profile_save_error = '';
      this.sync_profile_edit_form();
      this.is_editing_profile = true;
    },
    cancel_profile_edit() {
      this.profile_save_error = '';
      this.sync_profile_edit_form();
      this.is_editing_profile = false;
    },
    sanitize_profile_name(field_name) {
      this.profile_edit_form[field_name] = String(this.profile_edit_form[field_name] || '')
        .replace(/[^A-Za-z\s'-]/g, '')
        .slice(0, 50);
    },
    sanitize_profile_phone() {
      this.profile_edit_form.phone_number = String(this.profile_edit_form.phone_number || '')
        .replace(/\D/g, '')
        .slice(0, 10);
    },
    validate_profile_edit_form() {
      if (!this.profile_edit_form.first_name.trim()) return 'First name is required.';
      if (!has_valid_name(this.profile_edit_form.first_name)) return 'First name must be at least 2 letters and cannot contain numbers.';
      if (!this.profile_edit_form.last_name.trim()) return 'Last name is required.';
      if (!has_valid_name(this.profile_edit_form.last_name)) return 'Last name must be at least 2 letters and cannot contain numbers.';
      if (!this.profile_edit_form.phone_number.trim()) return 'Phone number is required.';
      if (!is_valid_phone_number(this.profile_edit_form.phone_number)) return 'Phone number must be exactly 10 digits.';
      return '';
    },
    async save_profile_edit() {
      const validation_message = this.validate_profile_edit_form();
      if (validation_message) {
        this.profile_save_error = validation_message;
        return;
      }

      this.is_saving_profile = true;
      this.profile_save_error = '';

      try {
        const updated_profile = await update_user_profile_details({
          user_id: this.profile_details?.id || this.current_user_id,
          first_name: this.profile_edit_form.first_name,
          last_name: this.profile_edit_form.last_name,
          phone_number: this.profile_edit_form.phone_number
        });

        this.profile_details = {
          ...this.profile_details,
          ...updated_profile,
          email: this.profile_details?.email || updated_profile.email
        };
        this.sync_profile_edit_form();
        this.is_editing_profile = false;

        if (this.profile_display_name !== this.$store.getters.currentLeadName) {
          this.$store.dispatch('setCurrentLeadName', this.profile_display_name);
        }
      } catch (error) {
        this.profile_save_error = String(error?.message || 'Unable to update your profile right now.');
      } finally {
        this.is_saving_profile = false;
      }
    },
    close_profile_modal() {
      this.show_profile_modal = false;
      this.is_editing_profile = false;
      this.profile_save_error = '';
    },
    handle_escape_key(event) {
      if (event?.key === 'Escape' && this.show_profile_modal) {
        this.close_profile_modal();
      }
    }
  }
};
